<template>
  <div class="i-container">
    <div
      class="comment-response-container"
      :class="{ active: commentResponse }"
    >
      <div v-if="commentResponse" class="comment-response-username">
        {{
          $t("comments.placeholder.responding_to") +
          " " +
          commentResponse.user.username
        }}
      </div>
      <div class="comment-response-close" @click="cancelReply">
        <i class="fas fa-times"></i>
      </div>
    </div>

    <div class="icons-container">
      <div v-for="icon in icons" class="icon-box">
        <div class="icon-text" @click="chooseIcon">{{ icon }}</div>
      </div>
    </div>

    <div class="comment-container">
      <div class="profile-pic-container">
        <CommentUserIcon :imageUrl="profile_pic" :logged="true" />
      </div>
      <div class="comment-input">
        <input
          class="comment-textarea"
          ref="textarea"
          :value="textInput"
          @change="changeInput"
          :placeholder="$t('comments.placeholder.write_comment')"
        />
      </div>
      <div class="button">
        <div class="button-text" @click="sendComment">
          {{ $t("comments.placeholder.publish") }}
        </div>
        <!-- <i class="icon" @click="sendComment" aria-hidden="true"></i> -->
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { Post } from "~/types/Post";

import resources from "@/utils/resources";
import _NuxtApp from "~/types/NuxtApp";
import { Comment } from "~/types/Comment";

const {
  $auth,
  $i18n: { t: $t },
} = <_NuxtApp>useNuxtApp();

const $emit = defineEmits<{
  (event: "sendMessage", message: string): void;
  (event: "cancelReply"): void;
}>();

// Props
const { post } = defineProps<{
  post: Post;
}>();

// Data
const commentResponse = ref<Comment | null>(null);
const textInput = ref("");
const profile_pic = ref<string>();
const icons = ["👍", "🔥", "❤️", "🥰", "😮", "😈", "💋", "💦"];

// Refs
const textarea = ref<HTMLElement | undefined>();

// Methods
function chooseIcon(e: MouseEvent) {
  // @ts-ignore
  const icon = e.target.innerText;
  textInput.value += icon;
}

function sendComment() {
  const message = textInput.value;

  let commentId = null;
  if (commentResponse.value) {
    commentId = commentResponse.value.id;
  }

  textInput.value = "";
  commentResponse.value = null;

  resources.comments
    .sendComment(post.post_id, message, commentId)
    .then((response) => {
      if (response.success) {
        $emit("sendMessage", response.data);
      }
    })
    .catch((err) => {
      if (err.status === 429) {
        // TODO: Show toast
        // $toast.error($t("comments.messages.too_many_comments"), {
        //   duration: 3000,
        //   position: "top-right",
        // });
      }
    });
}

function replyTo(comment: Comment) {
  textInput.value = `@${comment.user.username} `;
  textarea.value!.focus();

  commentResponse.value = comment;
}

function cancelReply() {
  commentResponse.value = null;
  textInput.value = "";
  $emit("cancelReply");
}

function changeInput(e: Event) {
  // @ts-ignore
  textInput.value = e.target.value;
}

onMounted(() => {
  textarea.value!.focus();

  $auth.user().then((user) => {
    if (user) profile_pic.value = user.profile_pic;
  });
});

defineExpose({
  replyTo,
  cancelReply,
});
</script>

<style scoped lang="scss">
@import "@/assets/scss/variables";

.i-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  margin: 0;
}

.comment-response-container {
  display: flex;
  flex-direction: row;
  align-items: start;
  justify-content: start;
  width: 100%;

  transition: all 0.25s;
  transform: translateY(100%);
  opacity: 0;
  height: 0;
  pointer-events: none;

  position: relative;
  z-index: 9;
  background-color: var(--background_01);

  &.active {
    transform: translateY(0);
    opacity: 1;
    height: unset;
    padding-block: 0.75em;
    pointer-events: all;
  }

  > .comment-response-username {
    margin: 0;
    margin-left: 1em;
    font-size: 0.9em;

    &::selection {
      background: none;
    }
  }

  > .comment-response-close {
    display: flex;

    width: 1.25em;
    height: 1.25em;
    margin-inline: auto 1em;
    margin-block: auto;

    cursor: pointer;

    > i {
      font-size: 1em;
      margin: auto;
    }
  }
}

.comment-container {
  display: flex;
  justify-content: center;
  width: 100%;

  margin: 0;
  padding-inline: 0.75em;
}

.icons-container {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
  width: 100%;
  margin: 0;
  padding: 0.5em 0;

  position: relative;
  z-index: 99;
  background-color: inherit;

  .icon-box {
    display: flex;
    height: 3em;
    width: 2em;
    cursor: pointer;
    flex-grow: 1;

    > .icon-text {
      font-size: 1.75em;
      transition: font-size 0.25s;
      text-align: center;
      margin: auto;

      &:hover {
        font-size: 2.25em;
      }

      &::selection {
        background: none;
      }
    }
  }
}

.button {
  display: flex;
  position: relative;
  text-align: center;
  /*background: #cccccc;*/
  cursor: pointer;
  min-width: fit-content;

  margin-left: 0.5em;

  &::selection,
  &:active {
    background: none;
  }

  > .button-text {
    width: fit-content;
    height: min-content;
    margin-top: auto;

    font-weight: 600;
    color: var(--pallete_main_color);
  }
}

.icon {
  font-size: 25px;
  vertical-align: bottom;

  margin: auto;

  // Image
  width: 28px;
  height: 28px;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("/img/btn/paper-plane.png");
}

.profile-pic-container {
  display: flex;
  margin-right: 0.5em;
  margin-block: auto;
}

.comment-input {
  display: flex;
  width: 100%;
  overflow: hidden;

  > .comment-textarea {
    width: 100%;
    height: min-content;
    margin-block: auto;
    resize: none;
    outline: none;

    text-wrap: nowrap;
    // overflow-y: scroll;

    // border-radius: 5px;
    // border: 1px solid var(--background_01);
    // background-color: var(--pallete_over_main_text_color);
    font: inherit;
    color: inherit;
    border: none;
    background: inherit;

    caret-shape: bar;
    caret-color: var(--pallete_main_color);
  }

  ::-webkit-scrollbar {
    display: none;
  }
}
</style>
